/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
// material
import { Switch, TextField, Group, IconControl, sendErrorMessage } from '@insticator/insticator-ui'; // iui-material
import ExpandableSection from 'material/expandablesection/expandablesection';
import SectionDescription from 'material/expandablesection/sectiondescription/sectiondescription';
import SectionCard from 'material/expandablesection/sectioncard/sectioncard';
import SectionGroup from 'material/expandablesection/sectiongroup/sectiongroup';
import SectionGroupDescription from 'material/expandablesection/sectiongroupdescription/sectiongroupdescription'
import ConfigController from 'material/expandablesection/controllers/configcontroller/configcontroller';
import TopArticles from 'material/community/toparticles/toparticles';
import Timeframe from 'material/community/timeframe/timeframe';
import PlaceholderImages from 'material/community/placeholderimages/placeholderimages';
import TopCommenters from 'material/community/topcommenters/topcommenters';
// actions
import { updateCommunity, updateCommunityData, cancelSectionChanges } from 'state/actions/commentingState/configurationStatus';
// utils
import { url } from 'utils/urls';
// styles
import injectSheet from 'react-jss';
import styles from 'pages/dashboard/commenting/configuration/communitysection/communitysection_styles';


const CommunitySection = (props) => {

    // action dispatch
    const dispatch = useDispatch();
    const updateCommunityState = (prop, value) => dispatch(updateCommunity(prop, value));
    const restoreStateOnCancel = (section) => dispatch(cancelSectionChanges(section));

    // redux state
    const isupdated = useSelector(({ commentingState }) => commentingState.configStatus.community.updated);
    const placeholderImageUploading = useSelector(({ commentingState }) => commentingState.addOnsStatus.isUploadingImage);
    const community = useSelector(({ commentingState }) => commentingState.configStatus.community);
    const savingCommunity = useSelector(({ commentingState }) => commentingState.configStatus.savingCommunity);
    const isCommunityEnabled = useSelector(({ commentingState }) => commentingState.configStatus.community.isCommunityEnabled);
    const title = useSelector(({ commentingState }) => commentingState.configStatus.community.title);
    const position = useSelector(({commentingState}) => commentingState.configStatus.community.position);
    const showTopArticles = useSelector(({ commentingState }) => commentingState.configStatus.community.showTopArticles);
    const topArticlesInitialLoadLimit = useSelector(({ commentingState}) => commentingState.configStatus.community.topArticlesInitialLoadLimit);
    const topArticlesDaysTimeFrame = useSelector(({ commentingState }) => commentingState.configStatus.community.topArticlesDaysTimeFrame);
    const selectedPlaceholderImageType = useSelector(({ commentingState }) => commentingState.configStatus.community.placeholderImageType);
    const placeholderImageUrl = useSelector(({ commentingState }) => commentingState.configStatus.community.placeholderImageUrl);
    const placeholderImageName = useSelector(({ commentingState }) => commentingState.configStatus.community.placeholderImageName);
    const showTopCommenters = useSelector(({ commentingState}) => commentingState.configStatus.community.showTopCommenters);
    const topCommentersInitialLoadLimit = useSelector(({ commentingState }) => commentingState.configStatus.community.topCommentersInitialLoadLimit);

    // component state
    const [isOpened, setIsOpened] = useState(false);
    const [communityState, setCommunityState] = useState(community);

    const titleTest = /^(?!\s)[\WA-Za-z0-9'\-\s]{0,64}$/;
    const validateTitle = e => updateCommunityState('title', {...title, valid: titleTest.test(e.target.value)});

    const collapse = () => {
        setIsOpened(false);
    }

    const handleSave = () => {
        const imageUrl = selectedPlaceholderImageType === 'standard' ? null : placeholderImageUrl !== '' ? placeholderImageUrl : null
        const communityCopy = community;
        communityCopy.placeholderImageUrl = imageUrl;
        communityCopy.placeholderImageType = selectedPlaceholderImageType;
        const communityData = {
            ...community,
            title: !!title.value ? title.value : null,
            topArticlesDaysTimeFrame: parseInt(topArticlesDaysTimeFrame.value),
            placeholderImageUrl: imageUrl,
            state: communityCopy
        }
        setCommunityState(community);
        dispatch(updateCommunityData(community.uuid, communityData, collapse));
    }

    const handleCancel = () => {
        restoreStateOnCancel('community');
    }

    const disableSave = !isupdated || placeholderImageUploading || (selectedPlaceholderImageType === 'custom' && (!placeholderImageUrl || placeholderImageUrl === ''));

    const isSectionValid = () => {
        let isValid = true;
        isValid = isValid && title.valid && topArticlesDaysTimeFrame.valid;
        return isValid
    }

    const saveCommunity = () => {
        if(!isSectionValid()){
            return dispatch(sendErrorMessage('Cannot save: invalid fields'));
        }
        handleSave();
    }

    const controller =  (
        <ConfigController
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            handleCancel={handleCancel}
            handleSave={saveCommunity}
            disableSave={disableSave}
            processing={savingCommunity}
            title='Community'
            description={
            <SectionDescription
                main={`${position.toLowerCase().replace(/(^\w|_\w)/g, m => m.toUpperCase()).replace('_', '-')} Tab`}
                additional='Top articles, +2 more'
            />}
        />
    );

    return (
        <ExpandableSection
            className={`communitysection ${props.classes.communitysection}`}
            controller={controller}
            isOpened={isOpened}
            setIsOpened={setIsOpened}
            handleCancel={handleCancel}
            handleSave={saveCommunity}
            disableSave={disableSave}
            processing={savingCommunity}
        >
            <SectionCard>
                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Community'}
                        description={'Community shows most popular discussions and top commenters on your website.'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateCommunityState('isCommunityEnabled', !isCommunityEnabled)}
                        on={isCommunityEnabled}
                        variant='secondary'
                    />
                </SectionGroup>

                <>
                    { isCommunityEnabled &&
                        <SectionGroup>
                            <SectionGroupDescription
                                title={'Position in the commenting unit'}
                            />
                            <Group name={'controls'}>
                                <IconControl
                                    text={'In-Unit Tab'}
                                    selected={position === 'IN_UNIT'}
                                    image={`${url.image}/illustrations/illustration-In-unit-tab.svg`}
                                    value={'IN_UNIT'}
                                    handleSelect={value => updateCommunityState('position', value)}
                                />
                                <IconControl
                                    text={'Above Unit'}
                                    selected={position === 'ABOVE_UNIT'}
                                    image={`${url.image}/illustrations/illustration-above-unit.svg`}
                                    value={'ABOVE_UNIT'}
                                    handleSelect={value => updateCommunityState('position', value)}
                                />
                                <IconControl
                                    text={'Below Unit'}
                                    selected={position === 'BELOW_UNIT'}
                                    image={`${url.image}/illustrations/illustration-below-unit.svg`}
                                    value={'BELOW_UNIT'}
                                    handleSelect={value => updateCommunityState('position', value)}
                                />
                            </Group>
                        </SectionGroup>
                    }
                    <SectionGroup>
                        <h2>Title</h2>
                        <TextField
                            type={'text'}
                            required={true}
                            name={'communityTitle'}
                            value={title.value}
                            valid={title.valid}
                            handleBlur={validateTitle}
                            handleChange={e => updateCommunityState('title', {...title, value: e.target.value})}
                            placeholder={'Popular in the community'}
                        />
                    </SectionGroup>
                </>

                <SectionGroup row>
                  <SectionGroupDescription
                      title={'Top articles'}
                      description={'Define how many top articles should be loaded by default.'}
                  />
                  <Switch
                      isStatic={true}
                      handleClick={() => updateCommunityState('showTopArticles', !showTopArticles)}
                      on={showTopArticles}
                      variant='secondary'
                  />
                </SectionGroup>


                {showTopArticles &&
                    <>
                        <SectionGroup secondary>
                            <SectionGroupDescription />
                            <TopArticles
                                section={'config'}
                                data={{
                                    topArticlesInitialLoadLimit
                                }}
                                onChange={value => updateCommunityState('topArticlesInitialLoadLimit', value)}
                            />
                        </SectionGroup>

                        <SectionGroup secondary>
                            <SectionGroupDescription title={'Top articles timeframe'} />
                            <Timeframe
                                section={'config'}
                                data={{
                                    topArticlesDaysTimeFrame
                                }}
                                onChange={value => updateCommunityState('topArticlesDaysTimeFrame', value)}
                            />
                        </SectionGroup>

                        <SectionGroup secondary>
                            <SectionGroupDescription title={'Placeholder images for the articles without a header image'} />
                            <PlaceholderImages
                                fileName={placeholderImageName}
                                handleSelect={value => updateCommunityState('placeholderImageType', value)}
                                selectedPlaceholderImageType={selectedPlaceholderImageType}
                                type={'COMMUNITY'}
                                placeholderImageUrl={placeholderImageUrl}
                                onChange={value => updateCommunityState('placeholderImageUrl', value)}
                            />
                        </SectionGroup>
                    </>
                }

                <SectionGroup row>
                    <SectionGroupDescription
                        title={'Top commenters'}
                        description={'Define how many top commenters should be loaded by default.'}
                    />
                    <Switch
                        isStatic={true}
                        handleClick={() => updateCommunityState('showTopCommenters', !showTopCommenters)}
                        on={showTopCommenters}
                        variant='secondary'
                    />
                </SectionGroup>


                {showTopCommenters &&
                    <SectionGroup secondary>
                       <SectionGroupDescription />
                        <TopCommenters
                            data={{
                                topCommentersInitialLoadLimit
                            }}
                            onChange={value => updateCommunityState('topCommentersInitialLoadLimit', value)}
                        />
                    </SectionGroup>
                }
            </SectionCard>

        </ExpandableSection>
    );
};

export default injectSheet(styles)(CommunitySection)
